/*
 * Ghostboy Wishlist — core engine (Phase 1)
 * ---------------------------------------------------------------------------
 * - localStorage-backed, guest-friendly, synced across tabs
 * - Binds heart toggles ([data-gb-wishlist-toggle]) and header count ([data-gb-wishlist-count])
 * - Fires Klaviyo events + profile sync ("save to Klaviyo profile")
 * - Cross-device App Proxy sync is LIVE for logged-in customers: theme.liquid sets
 *   KROWN.settings.wishlist.proxyUrl = '/apps/wishlist' (backend-workers/wishlist).
 *   POSTs are fire-and-forget (no response handling) — worker failures are invisible
 *   here. The customer metafield custom.wishlist re-seeds the list on EVERY page
 *   load, so removals carry localStorage tombstones (see mergeServer) to stop
 *   stale server copies resurrecting them, and a pending push is flushed with
 *   sendBeacon on pagehide. Contract details: backend-workers/CONTRACTS.md.
 *
 * Public API: window.GBWishlist { has, all, count, add, remove, toggle, clear, subscribe }
 */
(function () {
  'use strict';

  var KS = (window.KROWN && window.KROWN.settings) || {};
  var CFG = KS.wishlist || {};
  var STORAGE_KEY = CFG.storageKey || 'gb:wishlist:v1';
  var EVT = 'gb:wishlist:change';

  /* ---- storage helpers --------------------------------------------------- */
  function readStore() {
    try {
      var arr = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || '[]');
      return Array.isArray(arr) ? arr : [];
    } catch (e) { return []; }
  }
  function writeStore(items) {
    // Returns true only if the write actually landed, so callers can tell an
    // in-memory mutation apart from a persisted one and roll back on failure
    // (private-mode quota, disabled storage) instead of showing a false "saved".
    try { window.localStorage.setItem(STORAGE_KEY, JSON.stringify(items)); return true; }
    catch (e) { return false; }
  }

  /* Removal tombstones: id -> removedAt (ms). A tombstone newer than a server
   * item's addedAt means "the removal wins" — without this, the add-only merge
   * below resurrects every removal from the stale metafield copy. */
  var TOMBSTONE_KEY = CFG.tombstoneKey || 'gb:wishlist:removed:v1';
  var TOMBSTONE_TTL = 90 * 24 * 60 * 60 * 1000; // prune after 90 days
  function readTombstones() {
    try {
      var map = JSON.parse(window.localStorage.getItem(TOMBSTONE_KEY) || '{}');
      if (!map || typeof map !== 'object' || Array.isArray(map)) { return {}; }
      var now = Date.now(); var out = {};
      Object.keys(map).forEach(function (id) {
        if (typeof map[id] === 'number' && now - map[id] < TOMBSTONE_TTL) { out[id] = map[id]; }
      });
      return out;
    } catch (e) { return {}; }
  }
  function writeTombstones(map) {
    try { window.localStorage.setItem(TOMBSTONE_KEY, JSON.stringify(map)); } catch (e) {}
  }

  /* ---- state ------------------------------------------------------------- */
  var list = readStore();
  var index = buildIndex(list);
  var tombstones = readTombstones();
  var subscribers = [];

  function buildIndex(items) {
    var s = {};
    for (var i = 0; i < items.length; i++) { s[String(items[i].id)] = true; }
    return s;
  }
  function normalize(data) {
    return {
      id: String(data.id),
      handle: data.handle || '',
      variant: data.variant ? String(data.variant) : null,
      // Explicit size pick made on the wishlist page (option value, e.g. "M").
      // Separate from `variant` (the colourway/variant snapshot at save time)
      // so "never chose a size" stays distinguishable from "saved with the
      // default variant". Travels in the share link (assets/wishlist-share.js).
      size: (data.size != null && String(data.size).trim()) ? String(data.size).trim() : null,
      title: data.title || '',
      url: data.url || (data.handle ? '/products/' + data.handle : ''),
      image: data.image || '',
      price: (data.price !== '' && data.price != null) ? parseInt(data.price, 10) : null,
      addedAt: data.addedAt || Date.now()
    };
  }
  function persistAndEmit(meta) {
    var ok = writeStore(list);
    index = buildIndex(list);
    var detail = { list: list.slice(), count: list.length, meta: meta || {} };
    subscribers.forEach(function (fn) { try { fn(detail); } catch (e) {} });
    document.dispatchEvent(new CustomEvent(EVT, { detail: detail }));
    return ok;
  }

  /* ---- public API -------------------------------------------------------- */
  var API = {
    has: function (id) { return !!index[String(id)]; },
    all: function () { return list.slice(); },
    count: function () { return list.length; },
    add: function (data) {
      var item = normalize(data);
      if (index[item.id]) { return false; }
      var prevTombstone = tombstones[item.id];
      if (prevTombstone != null) { delete tombstones[item.id]; writeTombstones(tombstones); }
      list.unshift(item);
      if (!persistAndEmit({ action: 'add', item: item })) {
        // Storage write failed — roll back the optimistic add so the heart /
        // aria-pressed / count can't confirm a save that never persisted.
        list.shift();
        index = buildIndex(list);
        if (prevTombstone != null) { tombstones[item.id] = prevTombstone; writeTombstones(tombstones); }
        notifyStorageError();
        return false;
      }
      sideEffects('add', item);
      return true;
    },
    remove: function (id) {
      id = String(id);
      var removed = null;
      var at = -1;
      for (var i = 0; i < list.length; i++) {
        if (list[i].id === id) { removed = list[i]; at = i; break; }
      }
      if (!removed) { return false; }
      var prevTombstone = tombstones[id];
      tombstones[id] = Date.now();
      writeTombstones(tombstones);
      list.splice(at, 1);
      if (!persistAndEmit({ action: 'remove', item: removed })) {
        // Storage write failed — restore the item at its original position and
        // undo the tombstone so the removal isn't shown but lost on reload.
        list.splice(at, 0, removed);
        index = buildIndex(list);
        if (prevTombstone != null) { tombstones[id] = prevTombstone; } else { delete tombstones[id]; }
        writeTombstones(tombstones);
        notifyStorageError();
        return false;
      }
      sideEffects('remove', removed);
      return true;
    },
    // Record (or clear, with null) the shopper's size pick for a saved item.
    // Emits a 'size' change so other tabs / the server copy follow; the page
    // renderer ignores it because the count doesn't move.
    setSize: function (id, size) {
      id = String(id);
      var item = null;
      for (var i = 0; i < list.length; i++) { if (list[i].id === id) { item = list[i]; break; } }
      if (!item) { return false; }
      var next = (size != null && String(size).trim()) ? String(size).trim() : null;
      if (item.size === next) { return true; }
      var prev = item.size;
      item.size = next;
      if (!persistAndEmit({ action: 'size', item: item })) {
        item.size = prev;
        notifyStorageError();
        return false;
      }
      pushRemote();
      return true;
    },
    toggle: function (data) {
      if (index[String(data.id)]) { API.remove(data.id); return false; }
      API.add(data); return true;
    },
    clear: function () {
      var now = Date.now();
      list.forEach(function (i) { tombstones[i.id] = now; });
      writeTombstones(tombstones);
      list = []; persistAndEmit({ action: 'clear' }); sideEffects('clear', null);
    },
    subscribe: function (fn) { if (typeof fn === 'function') { subscribers.push(fn); } return API; }
  };

  /* ---- side effects: Klaviyo + remote sync ------------------------------- */
  function sideEffects(action, item) {
    trackKlaviyo(action, item);
    syncKlaviyoProfile();   // debounced
    pushRemote();           // debounced + inert until configured (Phase 2)
  }

  function klaviyo() { return window.klaviyo || window._learnq || null; }

  function trackKlaviyo(action, item) {
    var kl = klaviyo();
    if (!kl || !kl.push || !item) { return; }
    var name = action === 'add' ? 'Added to Wishlist' : action === 'remove' ? 'Removed from Wishlist' : null;
    if (!name) { return; }
    kl.push(['track', name, {
      ProductID: item.id,
      ProductName: item.title,
      Handle: item.handle,
      URL: item.url ? (window.location.origin + item.url) : '',
      ImageURL: item.image,
      Price: item.price != null ? item.price / 100 : null,
      Currency: CFG.currency || (KS.iso_code || '').trim() || 'MYR'
    }]);
  }

  var syncKlaviyoProfile = debounce(function () {
    var kl = klaviyo();
    if (!kl || !kl.push) { return; }
    var props = {
      'Wishlist Count': list.length,
      'Wishlist Items': list.slice(0, 50).map(function (i) { return i.title; }),
      'Wishlist Updated At': new Date().toISOString()
    };
    if (CFG.email) { props.$email = CFG.email; }
    kl.push(['identify', props]);
  }, 1200);

  /* ---- remote (App Proxy) — active when proxyUrl + loggedIn are set ------- */
  var pushPending = false;
  function sendList(useBeacon) {
    pushPending = false;
    if (!CFG.proxyUrl || !CFG.loggedIn) { return; }
    var payload = JSON.stringify({ items: list });
    try {
      if (useBeacon && navigator.sendBeacon) {
        navigator.sendBeacon(CFG.proxyUrl, new Blob([payload], { type: 'application/json' }));
        return;
      }
      window.fetch(CFG.proxyUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: payload,
        keepalive: true
      }).catch(function () {
        // Fire-and-forget: localStorage stays the source of truth, so a failed
        // push here is not a functional loss — just swallow the async rejection
        // (offline / flaky proxy) so it doesn't surface as an unhandled
        // rejection in noisy error-rate monitoring.
      });
    } catch (e) {}
  }
  var pushRemoteDebounced = debounce(function () { sendList(false); }, 1500);
  function pushRemote() { pushPending = true; pushRemoteDebounced(); }
  // The debounce is why removals used to vanish: navigate within 1.5s and the
  // timer dies with the page. Flush a pending push the moment the page hides.
  function flushPending() { if (pushPending) { sendList(true); } }
  window.addEventListener('pagehide', flushPending);
  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'hidden') { flushPending(); }
  });

  function mergeServer(serverList) {
    var serverArr = Array.isArray(serverList) ? serverList : [];
    var serverIds = {};
    serverArr.forEach(function (raw) { serverIds[String(raw.id)] = true; });
    var changed = false;
    serverArr.forEach(function (raw) {
      var item = normalize(raw);
      var removedAt = tombstones[item.id];
      // wishlist-extra-2: compare the tombstone against the RAW server addedAt
      // (missing => 0), NOT normalize's Date.now() default — otherwise a
      // timestamp-less server copy always looks "newer" than the removal and
      // resurrects a genuinely removed item. Date.now() stays the display/sort
      // default via normalize above.
      var serverAddedAt = raw.addedAt || 0;
      if (removedAt && removedAt > serverAddedAt) { return; } // removal is newer — don't resurrect
      if (removedAt) { delete tombstones[item.id]; writeTombstones(tombstones); } // re-added later on another device
      if (!index[item.id]) { list.push(item); index[item.id] = true; changed = true; }
    });
    if (changed) {
      list.sort(function (a, b) { return b.addedAt - a.addedAt; });
      writeStore(list);
    }
    // wishlist-1: local items the server doesn't know about (guest saves made
    // before login, or a brand-new / empty account) also need a push, even when
    // nothing above mutated the list. OR this into the return so init()'s
    // existing `if (merged) { pushRemote(); }` fires whenever local is ahead.
    var localAhead = list.some(function (i) { return !serverIds[i.id]; });
    return changed || localAhead;
  }

  /* ---- UI binding -------------------------------------------------------- */
  function label(key) {
    var fallback = key === 'remove' ? 'Remove from wishlist' : 'Save to wishlist';
    return (CFG.locales && CFG.locales[key]) || fallback;
  }
  function refreshButton(btn) {
    var on = !!index[String(btn.getAttribute('data-gb-id'))];
    var text = on ? label('remove') : label('add');
    btn.setAttribute('aria-pressed', on ? 'true' : 'false');
    btn.classList.toggle('is-active', on);
    btn.setAttribute('aria-label', text);
    btn.setAttribute('title', text);
    var lbl = btn.querySelector('.gb-wishlist-toggle__label');
    if (lbl) { lbl.textContent = text; }
  }
  function refreshButtons() {
    var nodes = document.querySelectorAll('[data-gb-wishlist-toggle]');
    for (var i = 0; i < nodes.length; i++) { refreshButton(nodes[i]); }
  }
  function refreshCount() {
    var n = list.length;
    var nodes = document.querySelectorAll('[data-gb-wishlist-count]');
    for (var i = 0; i < nodes.length; i++) {
      nodes[i].textContent = n;
      nodes[i].classList.toggle('is-empty', n === 0);
    }
  }
  function readButton(btn) {
    return {
      id: btn.getAttribute('data-gb-id'),
      handle: btn.getAttribute('data-gb-handle'),
      variant: btn.getAttribute('data-gb-variant'),
      title: btn.getAttribute('data-gb-title'),
      url: btn.getAttribute('data-gb-url'),
      image: btn.getAttribute('data-gb-image'),
      price: btn.getAttribute('data-gb-price')
    };
  }
  function onClick(e) {
    var btn = e.target.closest && e.target.closest('[data-gb-wishlist-toggle]');
    if (!btn) { return; }
    e.preventDefault();
    var added = API.toggle(readButton(btn));
    if (added) {
      btn.classList.remove('gb-wishlist-toggle--pulse');
      void btn.offsetWidth;                  // restart animation
      btn.classList.add('gb-wishlist-toggle--pulse');
      if (!CFG.loggedIn) { maybeLoginNudge(); }   // Phase 2: sign-in prompt
    }
  }
  /* ---- sign-in nudge (guests only) --------------------------------------
   * Non-blocking, dismissible toast shown the first time a guest saves an item
   * in a session. "Don't show again" is remembered for the browser session so
   * it never nags on every click. Links to the account login URL.
   */
  var NUDGE_KEY = 'gb:wishlist:nudge-dismissed';
  var nudgeShownThisLoad = false;

  function nudgeDismissed() {
    try { return window.sessionStorage.getItem(NUDGE_KEY) === '1'; } catch (e) { return nudgeShownThisLoad; }
  }
  function setNudgeDismissed() {
    try { window.sessionStorage.setItem(NUDGE_KEY, '1'); } catch (e) {}
  }

  function maybeLoginNudge() {
    if (CFG.loggedIn) { return; }
    if (nudgeShownThisLoad || nudgeDismissed()) { return; }
    if (document.querySelector('.gb-wishlist-nudge')) { return; }
    nudgeShownThisLoad = true;

    var loc = CFG.locales || {};
    var loginUrl = CFG.accountLoginUrl || '/account/login';

    var toast = document.createElement('div');
    toast.className = 'gb-wishlist-nudge';
    toast.setAttribute('role', 'status');
    toast.setAttribute('aria-live', 'polite');

    var msg = document.createElement('span');
    msg.className = 'gb-wishlist-nudge__text';
    msg.textContent = decodeEntities(loc.sign_in_prompt) || "Log in so you don't lose your wishlist";

    var actions = document.createElement('span');
    actions.className = 'gb-wishlist-nudge__actions';

    var cta = document.createElement('a');
    cta.className = 'gb-wishlist-nudge__cta';
    cta.href = loginUrl;
    cta.textContent = loc.sign_in_cta || 'Sign in';

    var close = document.createElement('button');
    close.type = 'button';
    close.className = 'gb-wishlist-nudge__close';
    close.setAttribute('aria-label', loc.dismiss || 'Dismiss');
    close.innerHTML = '<svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" focusable="false" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M6 6l12 12M18 6L6 18"/></svg>';

    function dismiss() {
      setNudgeDismissed();
      toast.classList.remove('is-visible');
      window.setTimeout(function () { if (toast.parentNode) { toast.parentNode.removeChild(toast); } }, 220);
    }
    close.addEventListener('click', dismiss);
    cta.addEventListener('click', setNudgeDismissed);

    actions.appendChild(cta);
    actions.appendChild(close);
    toast.appendChild(msg);
    toast.appendChild(actions);
    document.body.appendChild(toast);

    // animate in
    requestAnimationFrame(function () { toast.classList.add('is-visible'); });

    // No auto-dismiss: the nudge persists until the visitor explicitly closes it
    // (the "×" control), signs in (CTA), or leaves the page. The session guard
    // above (sessionStorage) keeps it from re-appearing once dismissed.
  }

  /* ---- storage-failure toast --------------------------------------------
   * Shown once when a localStorage write is rejected (private mode / quota /
   * disabled storage) so the failure isn't silent after add()/remove() rolled
   * back. Reuses the sign-in nudge component with a --error modifier. Guarded
   * against the sign-in nudge so the two toasts never stack.
   */
  function notifyStorageError() {
    if (document.querySelector('.gb-wishlist-nudge')) { return; }
    var loc = CFG.locales || {};
    var toast = document.createElement('div');
    toast.className = 'gb-wishlist-nudge gb-wishlist-nudge--error';
    toast.setAttribute('role', 'alert');

    var msg = document.createElement('span');
    msg.className = 'gb-wishlist-nudge__text';
    msg.textContent = decodeEntities(loc.storage_error) ||
      "Couldn't save — check your browser's storage settings";
    toast.appendChild(msg);

    document.body.appendChild(toast);
    requestAnimationFrame(function () { toast.classList.add('is-visible'); });
    window.setTimeout(function () {
      toast.classList.remove('is-visible');
      window.setTimeout(function () { if (toast.parentNode) { toast.parentNode.removeChild(toast); } }, 220);
    }, 4000);
  }

  /* ---- helpers ----------------------------------------------------------- */
  // Locale strings injected via Liquid `| t` are HTML-escaped (e.g. ' -> &#39;);
  // decode before showing as plain text so apostrophes etc. render correctly.
  function decodeEntities(str) {
    if (!str || str.indexOf('&') === -1) { return str; }
    var ta = document.createElement('textarea');
    ta.innerHTML = str;
    return ta.value;
  }
  function debounce(fn, wait) {
    var t;
    return function () {
      var ctx = this, args = arguments;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(ctx, args); }, wait);
    };
  }

  /* ---- cross-tab sync ---------------------------------------------------- */
  window.addEventListener('storage', function (e) {
    if (e.key !== STORAGE_KEY && e.key !== TOMBSTONE_KEY) { return; }
    list = readStore();
    index = buildIndex(list);
    tombstones = readTombstones();
    refreshButtons();
    refreshCount();
    // wishlist-extra-1: also notify in-page subscribers and re-dispatch the
    // change event so an open wishlist PAGE in ANOTHER tab re-renders its grid.
    // Deliberately NO writeStore here — the originating tab already persisted;
    // re-writing would ping-pong storage events between tabs forever.
    var detail = { list: list.slice(), count: list.length, meta: { action: 'sync' } };
    subscribers.forEach(function (fn) { try { fn(detail); } catch (err) {} });
    document.dispatchEvent(new CustomEvent(EVT, { detail: detail }));
  });

  /* ---- variant/colorway sync (wishlist-4) -------------------------------
   * The save button's data-gb-image/price/variant are seeded once from the
   * product default at render time. Mirror fx-estimates.js's VARIANT_CHANGE
   * pattern (MutationObserver + WeakSet + product-wrapper scoping) so a
   * colorway/variant switch keeps the snapshot the shopper actually sees —
   * so saves, the wishlist page and Klaviyo events reflect the chosen variant.
   */
  var PRODUCT_WRAPPER_SEL = '[data-js-product-item], .product-item-compact';
  function syncButtonFromVariant(pv) {
    // Nearest product tile first; fall back to the section (PDP save row).
    var scope = pv.closest(PRODUCT_WRAPPER_SEL) || pv.closest('.shopify-section');
    var btn = scope && scope.querySelector('[data-gb-wishlist-toggle]');
    var v = pv.currentVariant;
    if (!btn || !v) { return; }
    btn.setAttribute('data-gb-variant', String(v.id));
    if (v.price != null) { btn.setAttribute('data-gb-price', String(v.price)); }
    // Only override the image when this variant actually has its own media
    // (colorway options do; size-only options don't) — otherwise keep the
    // default the button was seeded with.
    if (v.featured_image && v.featured_image.src) {
      btn.setAttribute('data-gb-image', v.featured_image.src.replace(/(\.[a-z]+)(\?|$)/i, '_360x$1$2'));
    }
  }
  var boundWL = new WeakSet();
  function bindWL(pv) {
    if (boundWL.has(pv)) { return; }
    boundWL.add(pv);
    pv.addEventListener('VARIANT_CHANGE', function (e) { syncButtonFromVariant(e.target); });
    // Deep links (?variant=) fire their first VARIANT_CHANGE during element
    // upgrade, before this deferred script binds — resync once.
    if (pv.currentVariant) { syncButtonFromVariant(pv); }
  }
  function initVariantSync() {
    document.querySelectorAll('product-variants').forEach(bindWL);
    new MutationObserver(function (muts) {
      muts.forEach(function (m) {
        m.addedNodes.forEach(function (n) {
          if (n.nodeType !== 1) { return; }
          if (n.matches && n.matches('product-variants')) { bindWL(n); }
          if (n.querySelectorAll) { n.querySelectorAll('product-variants').forEach(bindWL); }
        });
      });
    }).observe(document.body, { childList: true, subtree: true });
  }

  /* ---- init -------------------------------------------------------------- */
  function init() {
    var merged = mergeServer(CFG.saved);     // server-injected (logged-in) list → local
    document.addEventListener('click', onClick);
    document.addEventListener(EVT, function () { refreshButtons(); refreshCount(); });
    initVariantSync();
    refreshButtons();
    refreshCount();
    if (merged) { pushRemote(); }            // reconcile merged set back to server
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.GBWishlist = API;
})();
